import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext/AuthContext";
import api from "../../../api/axiosInstance";

type UserRole = "User" | "Admin" | "SuperAdmin";

type ManagedUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
};

type UpdateManagedUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  newPassword?: string;
  confirmPassword?: string;
};

type CreateManagedUserPayload = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: UserRole;
};

type EditFormState = {
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  password: string;
  confirmPassword: string;
};

const normalizeRole = (value: unknown): UserRole => {
  if (typeof value !== "string") return "User";
  const lowered = value.toLowerCase();
  if (lowered === "superadmin") return "SuperAdmin";
  if (lowered === "admin") return "Admin";
  return "User";
};

const extractRole = (roles: string[] | undefined): UserRole => {
  if (!roles || roles.length === 0) return "User";
  return normalizeRole(roles[0]);
};

const mapUser = (user: any): ManagedUser => {
  const rolesRaw = Array.isArray(user?.roles)
    ? user.roles.filter((role: unknown): role is string => typeof role === "string")
    : [];

  return {
    id: String(user?.id ?? ""),
    email: String(user?.email ?? ""),
    firstName: String(user?.firstName ?? ""),
    lastName: String(user?.lastName ?? ""),
    roles: rolesRaw,
  };
};

export default function UserManagementPage() {
  const { isAuthenticated, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState<EditFormState | null>(null);
  const [modalMode, setModalMode] = useState<"create" | "edit">("edit");

  const fetchUsers = useCallback(async () => {
    try {
      setErrorMessage("");
      const response = await api.get("Auth/users");
      const payload = response.data?.data ?? response.data;
      if (!Array.isArray(payload)) {
        setUsers([]);
        setErrorMessage("Invalid users payload.");
        return;
      }
      setUsers(payload.map(mapUser));
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Failed to load users.";
      setErrorMessage(message);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isSuperAdmin) {
      navigate("/");
      return;
    }
    fetchUsers();
  }, [fetchUsers, isAuthenticated, isSuperAdmin, navigate]);

  const openEditModal = (user: ManagedUser) => {
    setModalMode("edit");
    setForm({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: extractRole(user.roles),
      password: "",
      confirmPassword: "",
    });
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setModalMode("create");
    setForm({
      firstName: "",
      lastName: "",
      email: "",
      role: "User",
      password: "",
      confirmPassword: "",
    });
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setForm(null);
  };

  const passwordValidationMessage = useMemo(() => {
    if (!form) return "";
    const hasNewPassword = form.password.trim().length > 0;
    const hasConfirmPassword = form.confirmPassword.trim().length > 0;

    if (modalMode === "create" && (!hasNewPassword || !hasConfirmPassword)) {
      return "Password and confirmation are required for new users.";
    }

    if (hasNewPassword !== hasConfirmPassword) {
      return "Both password fields are required when changing password.";
    }

    if (hasNewPassword && form.password !== form.confirmPassword) {
      return "Passwords do not match.";
    }

    return "";
  }, [form, modalMode]);

  const upsertUserRow = (updatedUser: ManagedUser) => {
    setUsers((prev) => {
      const existingIndex = prev.findIndex((user) => user.id === updatedUser.id);
      if (existingIndex === -1) return [updatedUser, ...prev];
      return prev.map((user) => (user.id === updatedUser.id ? updatedUser : user));
    });
  };

  const handleSave = async () => {
    if (!form) return;
    if (passwordValidationMessage) {
      setErrorMessage(passwordValidationMessage);
      return;
    }

    try {
      setErrorMessage("");
      let response;

      if (modalMode === "create") {
        const createPayload: CreateManagedUserPayload = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role,
          password: form.password,
          confirmPassword: form.confirmPassword,
        };
        response = await api.post("Auth/users", createPayload);
      } else {
        const updatePayload: UpdateManagedUserPayload = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          role: form.role,
        };

        const hasPassword = form.password.trim().length > 0;
        if (hasPassword) {
          updatePayload.newPassword = form.password;
          updatePayload.confirmPassword = form.confirmPassword;
        }

        response = await api.put(`Auth/users/${form.id}`, updatePayload);
      }

      const updatedRaw = response.data?.data ?? response.data;
      const updatedUser = mapUser(updatedRaw);
      upsertUserRow(updatedUser);

      closeEditModal();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Failed to update user.";
      setErrorMessage(message);
    }
  };

  const handleRoleChange = (event: SelectChangeEvent<UserRole>) => {
    const value = event.target.value as UserRole;
    setForm((prev) => (prev ? { ...prev, role: value } : prev));
  };

  const handleDeleteUser = async (user: ManagedUser) => {
    const userLabel = `${user.firstName} ${user.lastName}`.trim() || user.email;
    const confirmed = window.confirm(`Delete user "${userLabel}"?`);
    if (!confirmed) return;

    try {
      setErrorMessage("");
      await api.delete(`Auth/users/${user.id}`);
      setUsers((prev) => prev.filter((item) => item.id !== user.id));
    } catch (error: any) {
      const message = error?.response?.data?.message ?? "Failed to delete user.";
      setErrorMessage(message);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <div className="flex justify-end">
        <Button variant="contained" onClick={openCreateModal}>
          Create user
        </Button>
      </div>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Current role</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{`${user.firstName} ${user.lastName}`.trim() || "-"}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{extractRole(user.roles)}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => openEditModal(user)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteUser(user)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={isModalOpen} onClose={closeEditModal} fullWidth maxWidth="sm">
        <DialogTitle>
          {modalMode === "create" ? "Create user" : "Edit user"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="First name"
            value={form?.firstName ?? ""}
            onChange={(event) =>
              setForm((prev) =>
                prev ? { ...prev, firstName: event.target.value } : prev
              )
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Last name"
            value={form?.lastName ?? ""}
            onChange={(event) =>
              setForm((prev) =>
                prev ? { ...prev, lastName: event.target.value } : prev
              )
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label="Email"
            value={form?.email ?? ""}
            onChange={(event) =>
              setForm((prev) =>
                prev ? { ...prev, email: event.target.value } : prev
              )
            }
          />
          <div className="mt-3">
            <Select
              fullWidth
              size="small"
              value={form?.role ?? "User"}
              onChange={handleRoleChange}
            >
              <MenuItem value="User">User</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="SuperAdmin">SuperAdmin</MenuItem>
            </Select>
          </div>
          <TextField
            fullWidth
            margin="dense"
            label={
              modalMode === "create"
                ? "Password"
                : "New password (optional)"
            }
            type="password"
            value={form?.password ?? ""}
            onChange={(event) =>
              setForm((prev) =>
                prev ? { ...prev, password: event.target.value } : prev
              )
            }
          />
          <TextField
            fullWidth
            margin="dense"
            label={
              modalMode === "create"
                ? "Confirm password"
                : "Confirm password (optional)"
            }
            type="password"
            value={form?.confirmPassword ?? ""}
            onChange={(event) =>
              setForm((prev) =>
                prev ? { ...prev, confirmPassword: event.target.value } : prev
              )
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
